import React from 'react';

function ProjectPage({ C, Link, useBreakpoint }) {
	const { isMobile, isTablet, px } = useBreakpoint();
	const compact = isMobile || isTablet;

	return (
		<div style={{ paddingTop: 100 }}>
			{/* ── HEADER ── */}
			<div style={{ padding: `60px ${px} 48px` }}>
				<div className='section-label fade-up' style={{ marginBottom: 20 }}>
					The Project
				</div>
				<h1
					className='display-heading fade-up delay-1'
					style={{
						fontSize: compact ? 'clamp(30px,7vw,52px)' : 'clamp(38px,5vw,64px)',
						maxWidth: 700,
						marginBottom: 28,
					}}
				>
					Tulsa, 1921.
					<br />
					<span style={{ color: C.teal }}>The search for the missing.</span>
				</h1>
				<div
					style={{ width: 56, height: 3, background: C.teal, marginBottom: 28 }}
				/>
				<p
					style={{
						fontSize: compact ? 15 : 17,
						color: '#8AACAA',
						maxWidth: 660,
						lineHeight: 1.9,
					}}
				>
					The 1921 Tulsa Race Massacre destroyed the Greenwood District, one of
					the most prosperous Black communities in American history. Official
					records were suppressed. Death certificates were withheld. For over a
					century, the people who were killed have remained unnamed and
					uncounted.
				</p>
			</div>

			{/* ── ORIGIN STORY ── */}
			<div style={{ background: '#070F0D', padding: `60px ${px}` }}>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
						gap: compact ? 48 : 80,
					}}
				>
					<div>
						<h2
							style={{
								fontFamily: 'Playfair Display',
								color: 'white',
								fontSize: compact ? 22 : 30,
								marginBottom: 18,
							}}
						>
							How this project began
						</h2>
						<p
							style={{
								fontSize: 14,
								color: '#8AACAA',
								lineHeight: 1.9,
								marginBottom: 14,
							}}
						>
							The original approach used OCR to scan handwritten census
							documents, cursive records that proved extraordinarily difficult
							to parse reliably. After reaching only 1–2% of target accuracy,
							the methodology was reconsidered entirely.
						</p>
						<p style={{ fontSize: 14, color: '#6A8A86', lineHeight: 1.9 }}>
							The breakthrough came through IPUMS: structured, normalized
							microdata for the entire U.S. census, enabling population-level
							probabilistic analysis at a scale that handwriting recognition
							never could.
						</p>
					</div>

					{/* Phase tracker, updated numbers */}
					<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						{[
							{
								phase: 'Phase 1',
								title: 'OCR Attempt',
								status: 'Pivoted',
								note: 'Cursive recognition too unreliable at scale',
							},
							{
								phase: 'Phase 2',
								title: 'IPUMS Microdata',
								status: 'Complete',
								note: '1M+ records narrowed to 2,102, 1,739 active risk pool',
							},
							{
								phase: 'Phase 3',
								title: 'Manual Review',
								status: 'In Progress',
								note: '363 survivors confirmed (17.3%) · 735 no archival trace',
							},
							{
								phase: 'Phase 4',
								title: 'Community Validation',
								status: 'Planned',
								note: 'Descendant-led corroboration workflows',
							},
						].map(({ phase, title, status, note }) => {
							const statusColor =
								status === 'Complete'
									? '#6DBF67'
									: status === 'In Progress'
										? C.tealLight
										: status === 'Pivoted'
											? '#C88A50'
											: '#4A6A66';
							return (
								<div
									key={phase}
									style={{
										background: '#111E1C',
										border: '1px solid #1A2E2A',
										padding: '16px 20px',
										display: 'flex',
										gap: 14,
										alignItems: 'flex-start',
									}}
								>
									<div
										style={{
											fontFamily: 'DM Mono',
											fontSize: 9,
											color: '#4A6A66',
											letterSpacing: '0.1em',
											textTransform: 'uppercase',
											whiteSpace: 'nowrap',
											paddingTop: 3,
										}}
									>
										{phase}
									</div>
									<div style={{ flex: 1 }}>
										<div
											style={{
												fontWeight: 500,
												color: 'white',
												fontSize: 14,
												marginBottom: 3,
											}}
										>
											{title}
										</div>
										<div style={{ fontSize: 12, color: '#4A6A66' }}>{note}</div>
									</div>
									<div
										style={{
											fontFamily: 'DM Mono',
											fontSize: 9,
											letterSpacing: '0.08em',
											whiteSpace: 'nowrap',
											color: statusColor,
										}}
									>
										{status}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>

			{/* ── KEY FINDING ── */}
			<div style={{ padding: `60px ${px} 80px` }}>
				<div className='section-label' style={{ marginBottom: 18 }}>
					Key Finding
				</div>
				<h2
					className='display-heading'
					style={{
						fontSize: compact ? 24 : 'clamp(26px,3vw,40px)',
						marginBottom: 14,
					}}
				>
					Household disruption patterns
				</h2>
				<p
					style={{
						fontSize: 15,
						color: '#6A8A86',
						maxWidth: 620,
						marginBottom: 36,
						lineHeight: 1.9,
					}}
				>
					Every household is classified by which members are missing. Three
					categories are High risk, the strongest signals of violent disruption.
				</p>
				<div
					style={{
						overflowX: 'auto',
						WebkitOverflowScrolling: 'touch',
						margin: `0 -${px}`,
						padding: `0 ${px}`,
					}}
				>
					<table
						style={{
							width: '100%',
							minWidth: 540,
							borderCollapse: 'collapse',
							fontSize: 13,
						}}
					>
						<thead>
							<tr style={{ borderBottom: `1px solid ${C.teal}` }}>
								{['Category', 'Interpretation', 'Risk', 'HHs', 'Persons'].map(
									(h) => (
										<th
											key={h}
											style={{
												textAlign: 'left',
												padding: '10px 14px',
												fontFamily: 'DM Mono',
												fontSize: 9,
												letterSpacing: '0.12em',
												textTransform: 'uppercase',
												color: C.tealLight,
												whiteSpace: 'nowrap',
												background: '#0A1210',
											}}
										>
											{h}
										</th>
									),
								)}
							</tr>
						</thead>
						<tbody>
							{[
								// High risk rows first, updated counts from current dataset
								[
									'Entire Household',
									'Strong unexplained disappearance',
									'HIGH',
									'47',
									'129',
								],
								[
									'Large Mixed Household',
									'Large household disappearance, head, children, lodgers, family',
									'HIGH',
									'2',
									'14',
								],
								// Representative medium risk rows
								[
									'Head Only',
									'Primary earner disappearance or relocation',
									'MEDIUM',
									'72',
									'72',
								],
								['Parents', 'Married couple relocation', 'MEDIUM', '38', '76'],
								[
									'Head & Child(s)',
									'Household restructuring, possible relocation or widowhood',
									'MEDIUM',
									'16',
									'44',
								],
								[
									'Spouse & Child(s)',
									'Parent relocating with children',
									'MEDIUM',
									'12',
									'35',
								],
								// Representative low risk row
								[
									'Lodgers / Service Only',
									'Domestic worker turnover or boarding mobility',
									'LOW',
									'275',
									'275',
								],
							].map(([cat, interp, risk, hh, p], i) => (
								<tr
									key={cat}
									style={{
										borderBottom: '1px solid #1A2E2A',
										background: i % 2 === 0 ? '#111E1C' : '#0D1816',
										transition: 'background 0.15s',
									}}
									onMouseEnter={(e) =>
										(e.currentTarget.style.background = '#162220')
									}
									onMouseLeave={(e) =>
										(e.currentTarget.style.background =
											i % 2 === 0 ? '#111E1C' : '#0D1816')
									}
								>
									<td
										style={{
											padding: '13px 14px',
											fontWeight: 500,
											color: '#A0B0AE',
											whiteSpace: 'nowrap',
										}}
									>
										{cat}
									</td>
									<td
										style={{
											padding: '13px 14px',
											color: '#4A6A66',
											lineHeight: 1.5,
										}}
									>
										{interp}
									</td>
									<td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>
										<span className={`risk-${risk.toLowerCase()}`}>{risk}</span>
									</td>
									<td
										style={{
											padding: '13px 14px',
											fontFamily: 'DM Mono',
											fontSize: 12,
											color: C.tealLight,
										}}
									>
										{hh}
									</td>
									<td
										style={{
											padding: '13px 14px',
											fontFamily: 'DM Mono',
											fontSize: 12,
											color: '#6A8A86',
										}}
									>
										{p}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<div
					style={{
						marginTop: 12,
						fontFamily: 'DM Mono',
						fontSize: 9,
						color: '#2A4A46',
						letterSpacing: '0.1em',
					}}
				>
					PRELIMINARY DATA · SUBJECT TO REVISION AS MANUAL REVIEW CONTINUES ·
					FULL TABLE ON EXPLORE PAGE
				</div>

				<div
					style={{
						marginTop: 28,
						display: 'flex',
						flexDirection: isMobile ? 'column' : 'row',
						gap: 12,
					}}
				>
					<Link to='/explore' className='btn-primary'>
						Explore Full Data →
					</Link>
					<Link to='/whitepaper' className='btn-outline'>
						Read Methodology
					</Link>
				</div>
			</div>
		</div>
	);
}

export default ProjectPage;
